import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeftIcon, ShieldExclamationIcon } from '@heroicons/react/24/outline';
import { fairLendingApi } from '../lib/api';
import { useTeamId } from '../hooks/useTeamId';
import toast from 'react-hot-toast';

const SEVERITY_STYLES: Record<string, { bg: string; text: string }> = {
  HIGH: { bg: 'bg-red-100', text: 'text-red-800' },
  MEDIUM: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
  LOW: { bg: 'bg-green-100', text: 'text-green-800' },
};

const CATEGORY_LABELS: Record<string, string> = {
  PROHIBITED_BASIS: 'Prohibited Basis',
  GEOGRAPHIC_TARGETING: 'Geographic Targeting',
  STEERING: 'Steering',
  DISCOURAGEMENT: 'Discouragement',
};

export default function FairLendingContentFindingsPage() {
  const queryClient = useQueryClient();
  const { teamId } = useTeamId();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [reviewingId, setReviewingId] = useState<string | null>(null);
  const [reviewStatus, setReviewStatus] = useState('ACKNOWLEDGED');
  const [reviewNote, setReviewNote] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['content-findings-list', teamId, page, statusFilter, severityFilter, categoryFilter],
    queryFn: async () => {
      const params: any = { page, limit: 20 };
      if (statusFilter) params.status = statusFilter;
      if (severityFilter) params.severity = severityFilter;
      if (categoryFilter) params.category = categoryFilter;
      const res = await fairLendingApi.getContentFindings(teamId!, params);
      return res.data;
    },
    enabled: !!teamId,
  });

  const reviewMutation = useMutation({
    mutationFn: ({ id, status, reviewNote }: { id: string; status: string; reviewNote: string }) =>
      fairLendingApi.reviewContentFinding(teamId!, id, { status, reviewNote }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['content-findings-list'] });
      toast.success('Finding reviewed');
      setReviewingId(null);
      setReviewNote('');
    },
    onError: () => toast.error('Failed to update finding'),
  });

  const findings = data?.findings || [];
  const totalPages = data?.totalPages || 1;

  return (
    <div>
      <div className="mb-6">
        <Link to="/fair-lending" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-3">
          <ArrowLeftIcon className="h-4 w-4" />
          Fair Lending Dashboard
        </Link>
        <div className="flex items-center gap-2">
          <ShieldExclamationIcon className="h-6 w-6 text-ocean-600" />
          <h1 className="text-2xl font-bold text-gray-900">Content Screening Findings</h1>
        </div>
        <p className="mt-1 text-sm text-gray-600">
          Fair lending content findings from marketing compliance reviews
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="rounded-md border-gray-300 text-sm shadow-sm focus:border-ocean-500 focus:ring-ocean-500"
        >
          <option value="">All Statuses</option>
          <option value="OPEN">Open</option>
          <option value="ACKNOWLEDGED">Acknowledged</option>
          <option value="MITIGATED">Mitigated</option>
          <option value="FALSE_POSITIVE">False Positive</option>
        </select>
        <select
          value={severityFilter}
          onChange={(e) => { setSeverityFilter(e.target.value); setPage(1); }}
          className="rounded-md border-gray-300 text-sm shadow-sm focus:border-ocean-500 focus:ring-ocean-500"
        >
          <option value="">All Severities</option>
          <option value="HIGH">High</option>
          <option value="MEDIUM">Medium</option>
          <option value="LOW">Low</option>
        </select>
        <select
          value={categoryFilter}
          onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }}
          className="rounded-md border-gray-300 text-sm shadow-sm focus:border-ocean-500 focus:ring-ocean-500"
        >
          <option value="">All Categories</option>
          <option value="PROHIBITED_BASIS">Prohibited Basis</option>
          <option value="GEOGRAPHIC_TARGETING">Geographic Targeting</option>
          <option value="STEERING">Steering</option>
          <option value="DISCOURAGEMENT">Discouragement</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow">
        {isLoading ? (
          <div className="p-12 text-center">
            <div className="animate-pulse space-y-3">
              {[1, 2, 3].map((i) => <div key={i} className="h-12 bg-gray-100 rounded" />)}
            </div>
          </div>
        ) : findings.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <ShieldExclamationIcon className="mx-auto h-10 w-10 text-gray-300" />
            <p className="mt-3 text-sm text-gray-500">
              No content screening findings found.
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Severity</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phrase</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Document</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {findings.map((f: any) => {
                    const sev = SEVERITY_STYLES[f.severity] || SEVERITY_STYLES.LOW;
                    const isReviewing = reviewingId === f.id;

                    return (
                      <tr key={f.id} className="hover:bg-gray-50">
                        <td className="px-6 py-3">
                          <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${sev.bg} ${sev.text}`}>
                            {f.severity}
                          </span>
                        </td>
                        <td className="px-6 py-3 text-sm text-gray-600">
                          {CATEGORY_LABELS[f.category] || f.category}
                        </td>
                        <td className="px-6 py-3 text-sm text-gray-900 max-w-xs truncate" title={f.phrase}>
                          &ldquo;{f.phrase}&rdquo;
                        </td>
                        <td className="px-6 py-3 text-sm text-gray-600">
                          {f.documentName ? (
                            <Link to={`/reports/${f.reportId}`} className="text-ocean-600 hover:underline">
                              {f.documentName}
                            </Link>
                          ) : (
                            <Link to={`/reports/${f.reportId}`} className="text-ocean-600 hover:underline text-xs">
                              View Report
                            </Link>
                          )}
                        </td>
                        <td className="px-6 py-3">
                          <span className={`text-xs font-medium ${f.status === 'OPEN' ? 'text-orange-600' : 'text-gray-500'}`}>
                            {f.status.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="px-6 py-3 text-xs text-gray-500">
                          {new Date(f.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-3">
                          {f.status === 'OPEN' && !isReviewing && (
                            <button
                              onClick={() => setReviewingId(f.id)}
                              className="text-xs font-medium text-ocean-600 hover:text-ocean-700"
                            >
                              Review
                            </button>
                          )}
                          {isReviewing && (
                            <div className="space-y-1.5 min-w-[200px]">
                              <select
                                value={reviewStatus}
                                onChange={(e) => setReviewStatus(e.target.value)}
                                className="block w-full rounded border-gray-300 text-xs"
                              >
                                <option value="ACKNOWLEDGED">Acknowledged</option>
                                <option value="MITIGATED">Mitigated</option>
                                <option value="FALSE_POSITIVE">False Positive</option>
                              </select>
                              <input
                                type="text"
                                value={reviewNote}
                                onChange={(e) => setReviewNote(e.target.value)}
                                placeholder="Review note..."
                                className="block w-full rounded border-gray-300 text-xs"
                              />
                              <div className="flex gap-1">
                                <button
                                  onClick={() => { setReviewingId(null); setReviewNote(''); }}
                                  className="rounded px-2 py-1 text-xs text-gray-500 hover:bg-gray-100"
                                >
                                  Cancel
                                </button>
                                <button
                                  onClick={() => reviewMutation.mutate({ id: f.id, status: reviewStatus, reviewNote })}
                                  disabled={!reviewNote.trim() || reviewMutation.isPending}
                                  className="rounded px-2 py-1 text-xs bg-ocean-600 text-white hover:bg-ocean-700 disabled:opacity-50"
                                >
                                  Save
                                </button>
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200">
                <p className="text-sm text-gray-500">
                  Page {page} of {totalPages} ({data?.total} total)
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="rounded px-3 py-1 text-sm border border-gray-300 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="rounded px-3 py-1 text-sm border border-gray-300 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
